import { Button, Icon, Modal } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import type { Activity, ActivityLanguage, ActivityVariant } from '@funkey/types/graphql/graphql';
import CartItemFormFields from '~/features/cart/CartItemFormFields';
import type { I18n } from '@funkey/locales';
import { LoginBackgroundImage } from '~/components/layout/images';
import { Shape } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import useQuotationCardFormState from '~/features/cart/useQuotationCardFormState';
import useWishlistState from '~/features/wishlist/useWishlistState';
import useCartState from '~/features/cart/useCartState';
import { parseAsInteger, useQueryState } from 'nuqs';
type QuotationCardProps = {
  activity: {
    id: Activity['id'];
    name?: string | null;
    slug?: string | null;
    imagePath?: string | null;
    activityVariants: Pick<ActivityVariant, 'id'>[];
    activityLanguages: Pick<ActivityLanguage, 'id' | 'name' | 'code'>[];
  };
  variantId?: string;
  className?: string;
  onSubmit?: () => void;
};
export const QuotationCard: React.FC<QuotationCardProps> = ({
  activity,
  variantId,
  className,
  onSubmit
}) => {
  const i18n = useI18n();
  const wishlist = useWishlistState();
  const cart = useCartState();
  const [boostAdId] = useQueryState('boostAdId', parseAsInteger);
  const addToCartMutation = cart.useUpsertCartItemMutation();
  const modal = Modal.useState(false);
  const validVariantId = variantId && activity.activityVariants.some(v => v.id === variantId) ? Number(variantId) : activity.activityVariants[0]?.id != null ? Number(activity.activityVariants[0].id) : null;
  const initialLanguageId = (activity.activityLanguages.find(x => i18n.language === x.code.toLowerCase()) ?? activity.activityLanguages[0])?.id;
  const isInWishlist = wishlist.isInWishlist(Number(activity.id));
  const formState = useQuotationCardFormState({
    variantId: validVariantId,
    languageId: initialLanguageId != null ? Number(initialLanguageId) : null
  });
  return <div className={className}>
            <div className="mb-6 flex items-center justify-between">
                <Title as="h3" size="xl" weight="bold">
                    {i18n._('Create quotation')}
                </Title>
                <button type="button" className="hidden text-right text-sm font-medium text-turqoise-500 sm:block" onClick={modal.open}>
                    {i18n._('How does it work?')}
                </button>
            </div>
            <p className="mb-8 hidden text-sm font-normal text-blue-700 sm:block">
                {i18n._('quoation_card.description')}
            </p>
            <div className="mb-8">
                <CartItemFormFields variants={activity.activityVariants} languages={activity.activityLanguages} formState={formState} />
            </div>
            <div className="grid grid-cols-1 gap-3">
                <Button className="w-full" variant="tertiary" onClick={() => {
        if (!formState.validate()) return;
        addToCartMutation.mutateAsync({
          variantId: formState.values.variantId!,
          languageId: formState.values.languageId!,
          cartItemID: null,
          boostAdId
        });
        onSubmit?.();
      }} size="lg" isLoading={addToCartMutation.isPending} iconBefore="plus" data-testid="add-to-cart">
                    {i18n._('Add to quotation')}
                </Button>

                <Button className={cx('w-full', isInWishlist && 'text-red-500 hover:text-red-600')} variant="outline" onClick={() => {
        if (isInWishlist) {
          wishlist.remove(Number(activity.id));
          return;
        }
        if (!formState.validate()) return;
        wishlist.upsert({
          activityId: Number(activity.id),
          name: activity.name ?? '',
          slug: activity.slug ?? '',
          imagePath: activity.imagePath ?? null,
          variantId: formState.values.variantId!,
          languageId: formState.values.languageId!,
          addedLanguage: i18n.language
        });
        onSubmit?.();
      }} size="lg" iconBefore="heart" iconClassName={isInWishlist ? 'fill-red-500 text-red-500' : undefined} data-testid="add-to-wishlist">
                    {isInWishlist ? i18n._('wishlist.remove', undefined, {
          message: 'Remove from wishlist'
        }) : i18n._('wishlist.add', undefined, {
          message: 'Add to wishlist'
        })}
                </Button>
            </div>

            {modal.isOpen ? <Modal onClose={modal.close} className="w-full sm:max-w-3xl">
                    <Modal.Content className="relative flex flex-wrap overflow-hidden rounded-xl p-3 sm:p-3">
                        <div className="relative w-full overflow-hidden rounded-xl md:w-1/3" style={{
          aspectRatio: '4/3'
        }}>
                            <LoginBackgroundImage />
                        </div>
                        <div className="flex w-full flex-col justify-center gap-4 p-10 md:w-2/3">
                            {steps(i18n).map((s, index) => <div key={s.title} className="flex flex-col gap-2 whitespace-pre-wrap px-4">
                                    <Title size="lg" weight="bold" className="flex items-center gap-3">
                                        <div className="left-4 flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-base font-extrabold leading-none text-white">
                                            {index + 1}
                                        </div>
                                        {s.title}
                                    </Title>
                                    <div className="leading-6 text-primary *:leading-7">
                                        {s.text}
                                    </div>
                                </div>)}
                        </div>
                        <Shape type="halfCircle" className="absolute -top-[112px] right-[80px] z-0 hidden -rotate-90 md:block" color="fill-secondary" />

                        <Shape type="pacMan" className="-rotate-60 absolute -bottom-[70px] -left-[70px] z-0" />
                        <button type="button" onClick={modal.close} className="absolute right-3 top-3">
                            <Icon name="close" />
                        </button>
                    </Modal.Content>
                </Modal> : null}
        </div>;
};
const steps = (i18n: I18n) => [{
  title: i18n._('quotation_card.info.step1.title'),
  text: i18n._('quotation_card.info.step1.description')
}, {
  title: i18n._('quotation_card.info.step2.title'),
  text: i18n._('quotation_card.info.step2.description')
}, {
  title: i18n._('quotation_card.info.step3.title'),
  text: i18n._('quotation_card.info.step3.description')
}, {
  title: i18n._('quotation_card.info.step4.title'),
  text: i18n._('quotation_card.info.step4.description')
}];

// Default export for backward compatibility
export default QuotationCard;